/**
 * Poster matrix — arranges track columns into a phase × track grid for the
 * wide-viewport Learn layout. Pure and generic over the card type so it stays
 * decoupled from the page's `LessonCard` and trivially testable.
 *
 * Rows are phases (Foundation → Advanced), columns are the tracks in the order
 * they arrive. Only phases that actually hold a lesson appear as rows, so the
 * grid never renders an empty band.
 */

import type { Phase } from "@music-app/learning-system";

const PHASE_ORDER: readonly Phase[] = ["1", "2A", "2B+", "3"];

export interface PosterColumn<T> {
  track: string;
  label: string;
  lessons: T[];
}

export interface PosterLane<T> {
  track: string;
  label: string;
  /** Cards in this lane keyed by phase. Absent key = empty cell. */
  cells: Map<Phase, T[]>;
}

export interface PosterMatrix<T> {
  /** Phases with at least one card across all lanes, in canonical order. */
  phases: Phase[];
  lanes: PosterLane<T>[];
}

export function buildPosterMatrix<T extends { phase: Phase }>(
  columns: PosterColumn<T>[],
): PosterMatrix<T> {
  const present = new Set<Phase>();
  const lanes: PosterLane<T>[] = columns.map((column) => {
    const cells = new Map<Phase, T[]>();
    for (const lesson of column.lessons) {
      present.add(lesson.phase);
      const bucket = cells.get(lesson.phase) ?? [];
      bucket.push(lesson);
      cells.set(lesson.phase, bucket);
    }
    return { track: column.track, label: column.label, cells };
  });
  const phases = PHASE_ORDER.filter((p) => present.has(p));
  return { phases, lanes };
}
